import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Factory, Cog, Car, ShoppingBag, HeartPulse, FlaskConical, HardHat, Truck,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";

const INDUSTRIES = [
  {
    icon: Factory, name: "Manufacturing",
    summary: "Growing manufacturing businesses face a common set of leadership questions as they scale: how to protect margin, how to keep operations visible, and how to build a supply chain that can absorb shocks and support expansion.",
    focus: ["Improve operational efficiency", "Increase production visibility", "Strengthen supply chain resilience", "Reduce operational costs", "Support business expansion"],
  },
  {
    icon: Cog, name: "Engineering & Industrial",
    summary: "Engineering and industrial businesses live and die by execution. As they grow, leadership decisions turn to how to deliver projects predictably, use assets and capital well, and align teams across a widening footprint.",
    focus: ["Improve project execution", "Increase asset utilisation", "Strengthen operational control", "Improve collaboration across teams", "Support scalable growth"],
  },
  {
    icon: Car, name: "Automotive",
    summary: "The automotive industry is being reshaped by shifting demand, new mobility models and a rapidly evolving supply base. Leadership teams need to stay agile, protect quality and keep the supplier ecosystem working together.",
    focus: ["Improve manufacturing agility", "Increase quality", "Strengthen supplier collaboration", "Improve operational resilience", "Prepare for future growth"],
  },
  {
    icon: ShoppingBag, name: "Retail & Consumer Goods",
    summary: "Retail and consumer businesses compete on customer experience and margin. Growth exposes weaknesses in inventory, demand planning and channel mix that need to be addressed as leadership plans the next stage of the business.",
    focus: ["Improve customer experience", "Optimise inventory", "Support multi-channel growth", "Improve demand planning", "Protect margins"],
  },
  {
    icon: HeartPulse, name: "Healthcare",
    summary: "Healthcare organisations balance a rising patient standard with tightening regulation and sustained cost pressure. Leadership decisions focus on service, compliance and running the operation reliably at scale.",
    focus: ["Improve patient services", "Strengthen compliance", "Improve operational efficiency", "Protect sensitive information", "Support long-term sustainability"],
  },
  {
    icon: FlaskConical, name: "Pharmaceutical",
    summary: "Pharmaceutical businesses work under the highest standards of quality and compliance while planning for growth. Executive priorities centre on regulatory strength, manufacturing quality and a supply chain that can be trusted.",
    focus: ["Support regulatory compliance", "Improve manufacturing quality", "Strengthen supply chain planning", "Reduce operational risk", "Enable business growth"],
  },
  {
    icon: HardHat, name: "Infrastructure & Construction",
    summary: "Infrastructure and construction businesses manage large, capital-intensive projects where cost, schedule and governance carry real consequences. Leadership focuses on control, visibility and delivering to plan.",
    focus: ["Improve project governance", "Increase cost visibility", "Improve planning", "Strengthen execution", "Support long-term growth"],
  },
  {
    icon: Truck, name: "Logistics & Distribution",
    summary: "Logistics and distribution businesses compete on service, speed and cost. Growth places pressure on network design, warehouse operations and the ability to give customers a reliable view of what is happening to their goods.",
    focus: ["Improve delivery performance", "Increase warehouse efficiency", "Strengthen supply chain visibility", "Reduce logistics costs", "Improve customer service"],
  },
];

const CHALLENGES = [
  { title: "Business expansion creating operational complexity", body: "Ambition is moving faster than the operating model, and the systems around it, were built to support." },
  { title: "Technology investments without clear business outcomes", body: "Major commitments are being made without a defensible line to revenue, margin or growth." },
  { title: "Disjointed business processes", body: "Work and information are fragmented across functions that no longer speak to each other cleanly." },
  { title: "Difficulty scaling operations", body: "The organisation is running into ceilings that cannot be lifted without stronger leadership decisions." },
  { title: "Leadership visibility challenges", body: "The executive team lacks a straight, board-level view of value, cost and business risk." },
  { title: "Increasing cybersecurity concerns", body: "Exposure now sits on the board's agenda without a clear owner or a defensible plan." },
  { title: "AI uncertainty", body: "Pressure to act on AI without a clear, commercial view of where it actually creates value." },
  { title: "Vendor dependence", body: "Critical decisions are being shaped by suppliers rather than by the business itself." },
  { title: "Lack of technology governance", body: "Major choices are being made without the discipline and accountability the stakes require." },
  { title: "Growing operational costs", body: "Costs are climbing faster than the value the business can actually point to." },
];

const APPROACH = [
  { title: "Understand business priorities", body: "Start with the strategy, the market and the outcomes leadership is accountable for." },
  { title: "Review current technology landscape", body: "A candid read on current capability, cost and business risk against those priorities." },
  { title: "Identify operational challenges", body: "Surface the two or three constraints that are most holding the business back." },
  { title: "Develop a practical technology roadmap", body: "A prioritised, board-ready plan the business can genuinely execute and defend." },
  { title: "Support executive decision-making", body: "Provide senior, independent counsel on the calls that most shape the next stage." },
  { title: "Strengthen governance", body: "Put the oversight in place to keep decisions accountable and on course." },
  { title: "Guide business transformation", body: "Provide senior leadership through the change, from business case to adoption." },
  { title: "Measure business outcomes", body: "Hold results against the value that was promised, in the board's own terms." },
];

const OUTCOMES = [
  { title: "Business Growth", note: "Technology that opens new revenue and capacity." },
  { title: "Operational Excellence", note: "A leaner, faster and more resilient core." },
  { title: "Executive Visibility", note: "A clear board-level view of value and risk." },
  { title: "Investment Optimisation", note: "Every technology decision tied to a return." },
  { title: "Risk Reduction", note: "Cyber, delivery and vendor risk controlled." },
  { title: "Better Decision Making", note: "Clarity to decide with confidence." },
  { title: "Scalable Operations", note: "Systems built for the next stage of growth." },
  { title: "Long-Term Competitive Advantage", note: "Capability rivals cannot easily copy." },
];

const KNOW = ["Customers", "Operations", "Financial objectives", "Market conditions", "Leadership priorities"];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Executive Technology Advisory by Industry",
  serviceType: "Industry-focused Executive Technology Advisory",
  description: "Executive technology advisory grounded in the business realities of manufacturing, engineering, automotive, retail, healthcare, pharmaceutical, infrastructure and logistics, for growing businesses and family-owned enterprises across India, the UAE and the Gulf Region.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/industries",
};

export default function Industries() {
  return (
    <>
      <Seo
        title="Industries"
        description="Technology strategies built around business priorities, not industry labels. Aftab Azad advises executive teams in manufacturing, engineering, automotive, retail, healthcare, pharmaceutical, infrastructure and logistics across India, the UAE and the Gulf Region."
        path="/industries"
        keywords={["Executive Technology Advisor", "Fractional CIO", "Manufacturing Technology Advisor", "Family-Owned Business Advisor", "Enterprise Technology Leadership", "Technology Strategy Consultant", "Mid-Market CIO"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="industries-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Industries</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["Technology strategies built", "around your business,"]} lineClassName="font-light" />
            <KineticHeading lines={["not your industry label."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Every business has unique challenges, but growing organisations often face similar
            leadership decisions as they expand operations, improve efficiency, strengthen governance
            and prepare for the next stage of growth. Aftab Azad works with executive teams to
            ensure technology decisions support business priorities, regardless of industry.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="industries-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/services/executive-advisory" data-testid="industries-hero-advisory"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Explore Advisory Services
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — INDUSTRY EXPERTISE */}
      <section data-testid="industries-grid" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Industries we understand</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The business behind", "each sector."]} />
            </h2>
          </div>
          <div className="mt-16 grid gap-6 md:grid-cols-2">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.name} delay={(i % 2) * 0.06}>
                  <div data-testid={`industry-${ind.name.toLowerCase().replace(/[\s&]+/g, "-")}`} className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                        <Icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{ind.name}</h3>
                    </div>
                    <p className="mt-5 font-body leading-relaxed text-slate-600">{ind.summary}</p>
                    <div className="mt-6 border-t border-slate-100 pt-5">
                      <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Business focus</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {ind.focus.map((f) => (
                          <span key={f} className="rounded-full bg-navy/5 px-3 py-1.5 font-body text-sm font-medium text-navy">{f}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — COMMON EXECUTIVE CHALLENGES */}
      <section data-testid="industries-challenges" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Common business challenges</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Business challenges", "first. Technology", "challenges second."]} />
            </h2>
            <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-slate-600">
              Growing businesses face a familiar set of pressures. These are business challenges first,
              and technology challenges only as a consequence.
            </p>
          </div>
          <div className="mt-16 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {CHALLENGES.map((c) => (
              <Reveal key={c.title} className="border-t border-slate-200 pt-6">
                <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{c.title}</h3>
                <p className="mt-2 max-w-md font-body leading-relaxed text-slate-600">{c.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — HOW AFTAB HELPS */}
      <section data-testid="industries-approach" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">How executive advisory creates value</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["An advisory approach,", "not a fixed template."]} />
            </h2>
          </div>
          <div className="mt-16 max-w-3xl">
            {APPROACH.map((s, i) => (
              <Reveal key={s.title} className="relative flex gap-6 pb-10 last:pb-0">
                {i < APPROACH.length - 1 && <span aria-hidden className="absolute left-6 top-14 h-[calc(100%-2.5rem)] w-px bg-slate-200" />}
                <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-lg font-bold text-white">{i + 1}</div>
                <div className="pt-1.5">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — CROSS-INDUSTRY PERSPECTIVE */}
      <section data-testid="industries-cross" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Across India, the UAE and the Gulf Region</p>
              <h2 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
                <KineticHeadingInView lines={["Different markets,", "the same business", "priorities."]} />
              </h2>
              <div className="mt-8 space-y-5 font-body text-lg leading-relaxed text-slate-600">
                <p>
                  Industries differ across regions, but the business priorities of growing organisations
                  remain remarkably similar. Leadership teams need stronger direction, better governance,
                  practical technology strategies and long-term planning to move confidently into the
                  next stage of the business.
                </p>
                <p>
                  Aftab Azad helps organisations build those capabilities through executive advisory
                  and Fractional CIO leadership, drawing on direct experience of business conditions
                  across India, the UAE and the Gulf.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <div className="grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200">
                {["Stronger leadership", "Better governance", "Long-term planning"].map((h) => (
                  <div key={h} className="flex items-center gap-4 bg-white px-7 py-7">
                    <span className="h-2 w-2 flex-shrink-0 rounded-full bg-royal" />
                    <span className="font-heading text-lg font-semibold tracking-tight text-navy">{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — BUSINESS OUTCOMES */}
      <section data-testid="industries-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY INDUSTRY KNOWLEDGE MATTERS */}
      <section data-testid="industries-why" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why industry understanding matters</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Technology follows", "the business."]} />
            </h2>
            <p className="mt-8 font-body text-lg leading-relaxed text-slate-600">
              Technology should never be introduced simply because it is available. Sound business
              transformation begins with understanding customers, operations, financial objectives,
              market conditions and leadership priorities. Technology should always support those
              objectives, never dictate them.
            </p>
          </div>
          <div className="mt-14 grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
            {KNOW.map((k) => (
              <div key={k} className="flex items-center bg-white px-6 py-8">
                <h3 className="font-heading text-lg font-bold text-navy">{k}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — CLOSING */}
      <section data-testid="industries-closing" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -right-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">A closing thought</p>
          <blockquote className="mt-8 font-heading text-3xl font-light leading-[1.3] tracking-tight md:text-4xl">
            No two businesses are identical. The most effective technology strategies are those
            designed around business priorities, leadership goals and long-term growth. That is
            the foundation of every advisory engagement.
          </blockquote>
        </div>
      </section>

      <CTASection
        overline="Let's discuss your business"
        heading={["The challenges facing", "your business."]}
        body="Whether you're leading a manufacturing company, engineering business, healthcare organisation, logistics provider or a growing family-owned enterprise, let's explore how executive technology leadership can help your organisation achieve its next stage of growth."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}
